import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import samplesEditorModel from './SamplesEditorModel';
import samplesModel, { SAMPLES_MODE } from '../SamplesModel';
import './SamplesEditor.css';
import {
  CommonFormInput,
  FormInputErrorMessage,
  SingleFileInput,
} from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { FormItem, FormLabel } from '@/components/ui/form';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { SampleFormValues } from '../SamplesUploader/SamplesUploader';
import {
  createI18nObj,
  dpAssert,
  isAdminPage,
  rightLanguage,
  setValueToi18nObj,
} from '@/sharedTypes/helpers';
import { PiCheckLight, PiFileArrowDownLight } from 'react-icons/pi';
import { IconButton } from '@/components/ui/button';
import { PiTrashLight } from 'react-icons/pi';
import toast from '@/components/ui/custom-toast';
import { useTranslation } from 'react-i18next';
import FormHeader from '@/components/ui/popover-header';
import type { Sample, Category, Author } from '@/sharedTypes/Api';
import { apiSwagger } from '@/shared/api';

type SampleEditFormValues = SampleFormValues & { changeLog?: string };
const SamplesEditor = observer(() => {
  const form = useForm<SampleEditFormValues>({
    defaultValues: {
      author_en: samplesEditorModel.sample?.metadata.author?.en || '',
      author_ru: samplesEditorModel.sample?.metadata.author?.ru || '',
      name_en: samplesEditorModel.sample?.metadata.title?.en || '',
      name_ru: samplesEditorModel.sample?.metadata.title?.ru || '',
      category_en: samplesEditorModel.sample?.category?.en || '',
      category_ru: samplesEditorModel.sample?.category?.ru || '',
      changeLog: rightLanguage(samplesEditorModel.sample?.metadata.manualChangelog),
    },
  });
  const [currentLang, setCurrentLang] = React.useState('en');

  const ref = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  let audioTitle = samplesEditorModel.audioFile?.name;
  if (!audioTitle) {
    if (samplesEditorModel.sample?.audioFilename) {
      audioTitle = samplesEditorModel.sample.audioFilename;
    }
  }
  let midiTitle = samplesEditorModel.midiFile?.name;
  if (!midiTitle && samplesEditorModel.sample) {
    if (samplesEditorModel.sample.midiFilename) {
      midiTitle = samplesEditorModel.sample.midiFilename;
    }
  }
  samplesEditorModel.samplesEditorRef = ref.current;

  const onSubmit = async (values: SampleEditFormValues) => {
    try {
      dpAssert(samplesEditorModel.sample, 'Sample is undefined');

      const missingFields = [];
      if (!values.name_en) missingFields.push({ lang: 'en', field: 'name_en' });
      if (!values.name_ru) missingFields.push({ lang: 'ru', field: 'name_ru' });
      if (!values.author_en) missingFields.push({ lang: 'en', field: 'author_en' });
      if (!values.author_ru) missingFields.push({ lang: 'ru', field: 'author_ru' });
      if (!values.category_en) missingFields.push({ lang: 'en', field: 'category_en' });
      if (!values.category_ru) missingFields.push({ lang: 'ru', field: 'category_ru' });

      if (missingFields.length > 0) {
        missingFields.forEach(({ field }) => {
          form.setError(field as keyof SampleEditFormValues, { type: 'manual', message: t('form.required') });
        });
        const firstMissing = missingFields[0];
        if (firstMissing.lang !== currentLang) {
          setTimeout(() => setCurrentLang(firstMissing.lang), 100);
        }
        toast({
          title: t('toast.error_title'),
          description: t('form.required'),
          type: 'error',
        });
        return;
      }

       // --- AUTHOR ---
    // --- AUTHOR ---
    let authorObj: Author;
    if (values.author_en || values.author_ru) {
      // Fetch all authors
      const authorsRes = await apiSwagger.authors.authorsList();
      const authorsData = await authorsRes.json();
      const existingAuthor = authorsData.data.find(
        (a: Author) => a.en === values.author_en && a.ru === values.author_ru
      );

      if (existingAuthor) {
        authorObj = existingAuthor;
      } else {
        // Create new author
        const createRes = await apiSwagger.authors.authorsCreate({
          en: values.author_en,
          ru: values.author_ru,
        });
        const createdAuthor = await createRes.json();
        authorObj = createdAuthor.data;
      }
    } else {
      authorObj = samplesEditorModel.sample?.metadata.author;
    }

    // --- CATEGORY ---
    let categoryObj: Category;
    if (values.category_en || values.category_ru) {
      // Fetch all categories
      const categoriesRes = await apiSwagger.categories.categoriesList();
      const categoriesData = await categoriesRes.json();
      const existingCategory = categoriesData.data.find(
        (c: Category) => c.en === values.category_en && c.ru === values.category_ru
      );

      if (existingCategory) {
        categoryObj = existingCategory;
      } else {
        // Create new category
        const createRes = await apiSwagger.categories.categoriesCreate({
          en: values.category_en,
          ru: values.category_ru,
        });
        const createdCategory = await createRes.json();
        categoryObj = createdCategory.data;
      }
    } else {
      categoryObj = samplesEditorModel.sample?.category;
    }

    // --- Build new sample object ---
    const newSample: Sample = {
      ...samplesEditorModel.sample,
      metadata: {
        ...samplesEditorModel.sample.metadata,
        author: authorObj,
        title: {
          en: values.name_en,
          ru: values.name_ru,
        },
        manualChangelog:
          values.changeLog ?? samplesEditorModel.sample.metadata.manualChangelog,
      },
      category: categoryObj,
    };

      console.log('--- submitting form with sample:', newSample);

      await samplesEditorModel.sendUpdate(newSample);

      toast({
        title: t('toast.success_title'),
        description: t('toast.update_success_desc'),
        type: 'success',
      });
      samplesModel.currentMode = SAMPLES_MODE.SELECT;
      samplesEditorModel.reset();
    } catch (error) {
      toast({
        title: t('toast.error_title'),
        description: t('toast.update_error_desc'),
        type: 'error',
      });
      console.error('Error updating sample:', error);
    }
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='flex flex-col max-h-full  overflow-hidden'
      >
        <FormHeader
          title={t('samples.edit')}
          backButton={{
            testId: 'samples_editor_back_btn',
            onClick: () => {
              samplesModel.currentMode = SAMPLES_MODE.SELECT;
              samplesEditorModel.reset();
            },
          }}
          rightContent={
            <>
              <button
                className='cursor-pointer text-lg'
                onClick={() => setCurrentLang(currentLang === 'en' ? 'ru' : 'en')}
                type='button'
              >
                {currentLang === 'en' ? 'RU' : 'EN'}
              </button>
              <IconButton
                type='button'
                data-testid='samples_editor_delete_btn'
                onClick={async () => {
                  try {
                    await samplesEditorModel.deleteSelectedSample();
                    toast({
                      title: t('toast.success_title'),
                      description: t('toast.delete_success_desc'),
                      type: 'success',
                    });
                    samplesModel.currentMode = SAMPLES_MODE.SELECT;
                    samplesEditorModel.reset();
                  } catch (error) {
                    toast({
                      title: t('toast.error_title'),
                      description: t('toast.delete_error_desc'),
                      type: 'error',
                    });
                    console.error('Error deleting sample:', error);
                  }
                }}
              >
                <PiTrashLight size={24} />
              </IconButton>
              <IconButton type='submit'>
                <PiCheckLight size={24} />
              </IconButton>
            </>
          }
        />

        <Separator />
        <div className='flex flex-col p-3 gap-3 overflow-auto flex-1 max-h-full'>
          {/* Category (folder) */}
          <FormItem className='grid grid-cols-[1fr_3fr] gap-3'>
            <FormLabel className='font-medium text-black text-sm text-end self-center'>
              {t('samples.folder')}
            </FormLabel>
            <div>
              {currentLang === 'en' ? (
                <>
                  <CommonFormInput
                    key='category_en'
                    name='category_en'
                    registerOptions={{ required: t('form.required') }}
                    placeholder={t('samples.folder')}
                  />
                  <FormInputErrorMessage key='category_en_error' name='category_en' />
                </>
              ) : (
                <>
                  <CommonFormInput
                    key='category_ru'
                    name='category_ru'
                    registerOptions={{ required: t('form.required') }}
                    placeholder={t('samples.folder')}
                  />
                  <FormInputErrorMessage key='category_ru_error' name='category_ru' />
                </>
              )}
            </div>
          </FormItem>
          {/* Author */}
          <FormItem className='grid grid-cols-[1fr_3fr] gap-3'>
            <FormLabel className='font-medium text-black text-sm text-end self-center'>
              {t('samples.author')}
            </FormLabel>
            <div>
              {currentLang === 'en' ? (
                <>
                  <CommonFormInput
                    key='author_en'
                    name='author_en'
                    registerOptions={{ required: t('form.required') }}
                    placeholder={t('samples.author')}
                  />
                  <FormInputErrorMessage key='author_en_error' name='author_en' />
                </>
              ) : (
                <>
                  <CommonFormInput
                    key='author_ru'
                    name='author_ru'
                    registerOptions={{ required: t('form.required') }}
                    placeholder={t('samples.author')}
                  />
                  <FormInputErrorMessage key='author_ru_error' name='author_ru' />
                </>
              )}
            </div>
          </FormItem>
          {/* Title */}
          <FormItem className='grid grid-cols-[1fr_3fr] gap-3'>
            <FormLabel className='font-medium text-black text-sm text-end self-center'>
              {t('samples.title')}
            </FormLabel>
            <div>
              {currentLang === 'en' ? (
                <>
                  <CommonFormInput
                    key='name_en'
                    name='name_en'
                    registerOptions={{ required: t('form.required') }}
                    placeholder={t('samples.title')}
                  />
                  <FormInputErrorMessage key='name_en_error' name='name_en' />
                </>
              ) : (
                <>
                  <CommonFormInput
                    key='name_ru'
                    name='name_ru'
                    registerOptions={{ required: t('form.required') }}
                    placeholder={t('samples.title')}
                  />
                  <FormInputErrorMessage key='name_ru_error' name='name_ru' />
                </>
              )}
            </div>
          </FormItem>
          <FormItem className='grid grid-cols-[1fr_3fr] gap-3'>
            <FormLabel className='font-medium text-black text-sm text-end self-center'>
              MusicXML
            </FormLabel>
            <div className='flex flex-row items-center justify-between gap-1'>
              <div className='flex-1'>
                <Controller
                  control={form.control}
                  name='xml'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <SingleFileInput
                        testid='samples_editor_musicxml_input'
                        name='musicxml'
                        accept='.musicxml'
                        id='musicxml'
                        labelvalue={
                          value?.name ??
                          samplesEditorModel.sample?.musicxmlFilename
                        }
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        //@ts-ignore
                        onChange={async (e) => {
                          const uploadedFile = e.target.files?.[0];
                          if (uploadedFile) {
                            await samplesEditorModel.loadNewMusicxml(uploadedFile);
                          }
                          onChange(uploadedFile);
                        }}
                      />
                    );
                  }}
                />
              </div>

              {isAdminPage() && (
                <IconButton
                  type='button'
                  onClick={() => {
                    samplesEditorModel.downloadFile();
                  }}
                >
                  <PiFileArrowDownLight size={28} />
                </IconButton>
              )}
            </div>
          </FormItem>

          <FormItem className='grid grid-cols-[1fr_3fr] gap-3'>
            <FormLabel className='font-medium text-black text-sm text-end self-center'>
              MIDI
            </FormLabel>
            <div>
              <Controller
                control={form.control}
                name='midi'
                render={({ field: { onChange, value } }) => {
                  return (
                    <SingleFileInput
                      name='midi'
                      id='midi'
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      // value={value}
                      labelvalue={
                        value?.name ?? samplesEditorModel.sample?.midiFilename
                      }
                      accept='.mid'
                      testid='samples_editor_midi_input'
                      onChange={async (e) => {
                        const uploadedFile = e.target.files?.[0];
                        if (uploadedFile) {
                          await samplesEditorModel.midiFileLoaded(uploadedFile);
                        }
                        onChange(uploadedFile);
                      }}
                    />
                  );
                }}
              />
            </div>
          </FormItem>
          <div className='grid grid-cols-[1fr_3fr] gap-3'>
            <FormLabel className='text-end'>{t('samples.notes')}</FormLabel>
            <Textarea {...form.register('changeLog')} className='resize-none' />
          </div>
        </div>
      </form>
    </FormProvider>
  );
});

export default SamplesEditor;
